import * as React from 'react';
import { AuthState } from '../auth/auth.types';
import { AdminState } from '../admin/admin.types';
import CommonFeatures from '../commons/CommonFeatures';
import { isPackageDisabled } from '../util/permission.util';
import SelectField from 'material-ui/SelectField';
import MenuItem from 'material-ui/MenuItem';
import RefreshIndicator from 'material-ui/RefreshIndicator';
import { BusinessState } from 'src/business/business.types';
import RaisedButton from 'material-ui/RaisedButton';
import { PackageState } from './Types';
import { Doughnut } from 'react-chartjs-2';
import { VALID_CHECKINS, NO_SHOW } from './Constants';
import ReactDatePicker from 'react-datepicker';
import { DatePicker, Dialog, FlatButton } from 'material-ui';

type Props = {
  readonly authState: AuthState
  readonly adminState: AdminState
  readonly packageState: PackageState
  readonly businessState: BusinessState
  readonly toDashboard: () => void
  readonly getPackageReport: (from: number, to: number) => Promise<Response>
  readonly getPackages: () => Promise<Response>
};

type States = {
  readonly loadingPackages: boolean
  readonly loadingPackageReport: boolean
  readonly loadingPackagesError: string
  readonly loadingPackageReportError: string
  readonly from: number
  readonly to: number
  readonly loadingReports: boolean
  readonly loadingReportsError: string
  readonly selectedStaff?: any
  readonly selectedPackage?: any
  readonly staffNames?: string[]
  readonly staffData?: number[]
  readonly backgroundColors?: string[]
  readonly hoverColors?: string[]
  readonly warningDialogOpen: boolean
  readonly warningDialogMessage: string
};

class ReportComponent extends React.Component<Props, States> {

  constructor(props: Props) {
    super(props);

    this.state = {
      loadingPackages: false,
      loadingPackageReport: false,
      loadingPackagesError: '',
      loadingPackageReportError: '',
      from: undefined,
      to: undefined,
      loadingReports: false,
      loadingReportsError: '',
      warningDialogOpen: false,
      warningDialogMessage: ''
    };
  }

  componentDidMount() {
    if (isPackageDisabled(this.props.authState, this.props.adminState)) {
      this.props.toDashboard();
    } else {
      this.setState({
        loadingPackages: true
      }, () => {
        this.props.getPackages().then(res => {
          switch (this.props.packageState.statusCode) {
            case 200:
              return this.setState({
                loadingPackagesError: '',
                loadingPackages: false
              });
            case 403:
              return this.setState({
                loadingPackagesError: 'Failed to load pakcages',
                loadingPackages: false
              });
            default:
              return this.setState({
                loadingPackages: false
              });
          }
        });
      });
    }
  }

  componentWillReceiveProps(nextProps: Props) {
    if (this.props !== nextProps && 
      nextProps.packageState.packageReport && 
      nextProps.packageState.packageReport.details
    ) {
      const { packageState, businessState } = nextProps;

      this.setState({
        staffNames: packageState.packageReport.details
          .map(d => d && businessState.business && 
            businessState.business.accounts.find(a => a && a.id === d.staffId))
          .map(d => d && d.firstName + ' ' + d.lastName),
        staffData: packageState.packageReport.details
          .filter(d => d && businessState.business && 
            businessState.business.accounts.find(a => a && a.id === d.staffId))
          .map(d => d.noShows + d.validCheckins)
      }, () => this.setState({
        backgroundColors: [...this.state.staffNames.keys()].map(i => this.getRandomColor()),
        hoverColors: [...this.state.staffNames.keys()].map(i => this.getRandomColor())
      }));
    }  
  }

  componentWillUnmount() {
    this.setState({
      loadingPackages: false,
      loadingReports: false,
      from: undefined,
      to: undefined,
      loadingReportsError: '',
      selectedStaff: null,
      selectedPackage: null
    });
  }

  render() {
    const { from, to, loadingReports, loadingReportsError } = this.state;

    return(
      <CommonFeatures
        authState={this.props.authState}
        businessState={this.props.businessState}
      >
        
        <div className="page-header-row row mt-2 mb-3">
          <div className="col-auto">
            <h3>Package Report</h3>
          </div>
        </div>

        <div className="row">
          <div className="col-12">
            <hr />
          </div>
        </div>

        <div className="row">
          {loadingReportsError ?
            <b className="col-12 red">{loadingReportsError}</b> :
            null
          }
          <div className="col-auto">
            <b>From</b>
            <ReactDatePicker
              selected={from === undefined ? undefined : from}
              onChange={(date) => this.handleFromFieldChange(date)}
            />
            <Dialog
              title="Warning"
              open={this.state.warningDialogOpen}
              actions={[ this.renderOkButton() ]}
              onRequestClose={() => this.setState({warningDialogOpen: false})}
            >
              {<b>{this.state.warningDialogMessage}</b>}
            </Dialog>
          </div>
          <div className="col-auto">
            <b>To</b>
            <ReactDatePicker
              selected={to === undefined ? undefined : to}
              onChange={(date) => this.handleToFieldChange(date)}
            />
          </div>
          <div className="col-auto"> 
            <br/>
            <RaisedButton
              label="SEARCH"
              primary={true}
              onClick={this.onSearchClick}
            />
          </div>
        </div>

        <div className="row">
          <div className="col-12">
            <hr />
          </div>
        </div>

        <div className="row mt-2 mb-2">
          <div className="col-md-12 col-sm-12 text-center">
            <RaisedButton
              label="Back"
              secondary={true}
              onClick={() => this.handleBack()}
              disabled={!this.state.selectedStaff && !this.state.selectedPackage}
            />
          </div>
        </div>
        <div className="row mt-2 mb-2 justify-content-center">
          {loadingReports ?
            <RefreshIndicator
              size={40}
              left={10}
              top={0}
              status="loading"
              style={{
                display: 'inline-block',
                position: 'relative',
              }}
            /> :
            this.renderAllCharts()
          }
        </div>
      </CommonFeatures>
    );
  }

  private handleBack = () => {
    if (this.state.selectedPackage) {
      this.setState({ selectedPackage: undefined });
    } else {
      this.setState({ selectedStaff: undefined });
    }
  }

  private renderAllCharts = () => {
    const { selectedPackage, selectedStaff } = this.state;

    if (!selectedStaff && !selectedPackage) {
      return(
        <div className="col-md-10 col-sm-10 text-center">
          <b>Business</b>
          {this.renderBusinessCharts()}
        </div>
      );
    } else if (selectedStaff && !selectedPackage) {
      return(this.renderStaffCharts());
    } else if (selectedStaff && selectedPackage) {
      return(this.renderPackageCharts());
    } else {
      return(null);
    }
  }

  private renderPackageCharts = () => {
    const { packageState, businessState } = this.props;
    const { selectedStaff, selectedPackage } = this.state;

    if (packageState.packageReport && packageState.packages && selectedStaff && selectedPackage) {
      const foundStaffDetail = packageState.packageReport.details.find(d => {
        const s = businessState.business.accounts.find(a => a.id === d.staffId);
        return s && (s.firstName + ' ' + s.lastName) === selectedStaff;
      });

      const pack = packageState.packages.find(p => p.chartLabel === selectedPackage);
      const foundPackageDetail = foundStaffDetail && 
        foundStaffDetail.byPackages.find(bp => pack && bp.packageId === pack.id);

      if (foundPackageDetail) {
        return(
          <div className="col-md-10 col-sm-10 text-center">
            <b>{pack && pack.description}</b>
            <Doughnut 
              data={{
                labels: [
                  NO_SHOW,
                  VALID_CHECKINS
                ],
                datasets: [{
                  data: [
                    foundPackageDetail.noShows,
                    foundPackageDetail.checkins
                  ],
                  backgroundColor: [
                    '#FF6384',
                    '#36A2EB'
                  ],
                  hoverBackgroundColor: [
                    '#FF6384',
                    '#36A2EB'
                  ]
                }]
              }}
            />
          </div>
        );
      } else {
        return(null);
      }
    } else {
      return(null);
    }
  }

  private renderStaffCharts = () => {
    const { selectedStaff } = this.state;
    const { packageState, businessState } = this.props;

    if (packageState.packageReport && 
      businessState.business && 
      businessState.business.accounts && 
      selectedStaff) {
      const foundStaffDetail = packageState.packageReport.details.find(d => {
        const s = businessState.business.accounts.find(a => a.id === d.staffId);
        return s && (s.firstName + ' ' + s.lastName) === selectedStaff;
      });

      const staff = businessState.business.accounts.find(a => foundStaffDetail && a.id === foundStaffDetail.staffId);
      
      const packageChartLabels = foundStaffDetail.byPackages.map(p => {
        const pack = packageState.packages.find(pa => pa.id === p.packageId);
        return pack && pack.chartLabel;
      });

      const packageDatas = foundStaffDetail.byPackages.map(p => p && p.noShows + p.checkins);

      const backgroundColors = [...packageChartLabels.keys()].map(i => this.getRandomColor());
      const hoverColors = [...packageChartLabels.keys()].map(i => this.getRandomColor());

      return(
        <div className="col-md-10 col-sm-10 text-center">
          <b>{staff && staff.firstName + ' ' + staff.lastName}</b>
          <Doughnut
            onElementsClick={(e: any) => this.setState({ selectedPackage: e && e.length > 0 && e[0]._model.label })}
            data={{
              labels: packageChartLabels,
              datasets: [{
                data: packageDatas,
                backgroundColor: backgroundColors,
                hoverBackgroundColor: hoverColors
              }]
            }}
          />
        </div>
      );
    } else {
      return(null);
    }
  }

  private handleFromFieldChange = (fromInput: number) => {
    this.setState(
      {
        loadingReports: false,
        from: fromInput
      }
    );
  }

  private handleToFieldChange = (toInput: number) => {
    this.setState(
      {
        loadingReports: false,
        to: toInput
      }
    );
  }

  private onSearchClick = () => {
    const { from, to } = this.state;

    if (from === undefined) {
      this.setState(
        {
          warningDialogOpen: true,
          warningDialogMessage: 'Please select when you want to search from'
        }
      );
      return;
    }
    if (to === undefined) {
      this.setState(
        {
          warningDialogOpen: true,
          warningDialogMessage: 'Please select when you want to search to'
        }
      );
      return;
    }
    if (from >= to) {
      this.setState(
        {
          warningDialogOpen: true,
          warningDialogMessage: 'From date should be earlier than To date.'
        }
      );
      return;
    }
    this.setState({
      loadingPackageReport: true
    }, () => {
      this.props.getPackageReport(this.state.from, this.state.to).then(res => {
        switch (this.props.packageState.statusCode) {
          case 200:
            return this.setState({
              loadingPackageReportError: '',
              loadingPackageReport: false
            });
          case 403:
            return this.setState({
              loadingPackageReportError: 'Failed to load pakcage report',
              loadingPackageReport: false
            });
          default:
            return this.setState({
              loadingPackageReport: false
            });
        }
      });
    });

  }

  private getRandomColor = () => {
    var letters = '0123456789ABCDEF';
    var color = '#';
    for (var i = 0; i < 6; i++) {
      color += letters[Math.floor(Math.random() * 16)];
    }
    return color;
  }

  private renderBusinessCharts = (): JSX.Element => {
    const { packageState, businessState } = this.props;
    const { staffNames, staffData, backgroundColors, hoverColors } = this.state;

    if (packageState.packageReport && businessState.business) {
      return(
        <Doughnut
          onElementsClick={(e: any) => this.setState({ selectedStaff: e && e.length > 0 && e[0]._model.label })}
          data={{
            labels: staffNames,
            datasets: [{
              data: staffData,
              backgroundColor: backgroundColors,
              hoverBackgroundColor: hoverColors
            }]
          }}
        />
      );
    } else {
      return(null);
    }
  }

  private renderOkButton = () => {
    return(
      <FlatButton
        label="Yes"
        primary={true}
        onClick={this.closeWarningModal}
      />
    );
  }

  private closeWarningModal = () => {
    this.setState({
      warningDialogOpen: false
    });
  }

}

export default ReportComponent;



// WEBPACK FOOTER //
// ./src/mms-package/ReportComponent.tsx